import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { EventModel, EventOptionGroup, EventParticipant, OddsSorting, TeamGroupingValues } from '@frontend/sports/betting-offer/feature/model';
import { ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TeamGrouping } from '@frontend/sports/types/components/sitecore';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { Subscription } from 'rxjs';

import { TabControllerFacadeService } from '../../sheet-view-core/store/tab-controller-facade.service';
import { TabControllerResponseState } from '../../sheet-view-core/store/tab-controller.model';
import { OptionGroupTrackingService } from '../option-group-tracking.service';
import { OptionGroupService } from '../option-group.service';
import { SheetViewService } from '../sheet-view.service';

@Component({
    selector: 'ms-team-grouping-tab-bar',
    templateUrl: './team-grouping-tab-bar.component.html',
    styleUrl: './team-grouping-tab-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ScrollAdapterComponent, DsSegmentedControlModule],
})
export class TeamGroupingTabBarComponent implements OnChanges {
    private shortTeamNamesService = inject(ShortTeamNamesService);
    private optionGroupService = inject(OptionGroupService);
    private siteCore = inject(Sitecore);
    private epcotConfig = inject(EpcotConfig);
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private changeDetector = inject(ChangeDetectorRef);

    @HostBinding('class') className = 'team-grouping-var-scope';

    @Input() event: EventModel;
    @Input() optionGroup: EventOptionGroup;
    @Output() teamGroupingChange = new EventEmitter<{ optionGroup: EventOptionGroup; controllerTabName?: string }>();

    teamGroupingOptions: { id: number; name: string }[] = [];
    selectedId?: number;
    epcotEnabled = this.epcotConfig.isEnabled;
    tabControllerState: TabControllerResponseState | null;
    marketUniqueId = '';
    private controllerTabName: string;
    private readonly destroyRef = inject(DestroyRef);
    private tabControllerFacadeService = inject(TabControllerFacadeService);
    private sheetViewService = inject(SheetViewService);
    private tabControllerSubscriptions: Record<string, Record<string, Subscription>> = {};

    ngOnChanges(changes: ISimpleChanges<TeamGroupingTabBarComponent>): void {
        if (changes.optionGroup || changes.event) {
            this.setTeamGroupingOptions();
            this.handleTabControllerStateChange();
        }
    }

    handleTabControllerStateChange() {
        if (this.sheetViewService.isEnabled) {
            this.marketUniqueId = this.optionGroup.detailedGrouping.marketGroupId;
            this.tabControllerSubscriptions[this.event.id] ??= {};
            this.tabControllerSubscriptions[this.event.id][this.marketUniqueId]?.unsubscribe();

            this.tabControllerSubscriptions[this.event.id][this.marketUniqueId] = this.tabControllerFacadeService
                .getTabControllerState(this.optionGroup.detailedGrouping.marketGroupId, this.event.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((data: TabControllerResponseState | null) => {
                    if (data) {
                        this.tabControllerState = data;
                        const candidateId = this.tabControllerState?.[this.tabControllerState?.lastSelectedTabIndex]?.controllerId;
                        this.selectedId = this.teamGroupingOptions?.some((opt) => opt.id === candidateId)
                            ? candidateId
                            : this.teamGroupingOptions[0].id;
                    } else {
                        const payload = {
                            marketGroupId: this.marketUniqueId,
                            controllerId: this.teamGroupingOptions[0]?.id,
                            eventId: this.event.id,
                        };
                        this.selectedId = this.teamGroupingOptions[0]?.id;
                        this.tabControllerFacadeService.createOrUpdateController(payload);
                    }

                    this.onTeamGroupingChange(this.selectedId);
                    this.changeDetector.markForCheck();
                });
            return;
        }

        if (this.selectedId) {
            this.onTeamGroupingChange(this.selectedId);
        } else {
            //highlighting first tab on initialization
            this.selectedId = this.teamGroupingOptions[0]?.id;
        }
    }

    onTeamGroupingChange(selectedId: number | string, shouldTrack = false): void {
        if (!this.teamGroupingOptions.length) return;

        let selectedControllerId: number | undefined = typeof selectedId === 'string' ? parseInt(selectedId) : selectedId;

        if (this.sheetViewService.isEnabled) {
            const lastSelectedControllerId = this.tabControllerState?.[this.tabControllerState?.lastSelectedTabIndex]?.controllerId;
            const payload = {
                marketGroupId: this.marketUniqueId,
                controllerId:
                    this.tabControllerState && selectedControllerId !== lastSelectedControllerId
                        ? selectedControllerId
                        : this.teamGroupingOptions[0].id,
                eventId: this.event.id,
            };
            if (this.tabControllerState) {
                if (selectedControllerId !== lastSelectedControllerId) {
                    this.tabControllerFacadeService.createOrUpdateController(payload);
                }
            } else {
                this.tabControllerFacadeService.createOrUpdateController(payload);
            }
        }
        if (isNaN(selectedControllerId)) {
            selectedControllerId = undefined;
        }
        this.selectedId = selectedControllerId;
        this.controllerTabName = this.teamGroupingOptions.find((controller) => controller.id === selectedControllerId)?.name!;
        const optionGroup = this.getOptionGroup();
        this.teamGroupingChange.next({ optionGroup, controllerTabName: this.controllerTabName });

        if (shouldTrack) {
            this.optionGroupTrackingService.trackCollapseExpandClick(
                this.event.sport.name + '-sub navigation',
                'click',
                this.event.participants,
                this.optionGroup.name,
                this.controllerTabName,
            );
        }
    }

    private setTeamGroupingOptions(): void {
        const { homeTeam, awayTeam } = this.getHomeAndAwayTeam();
        const teamGrouping = this.optionGroup.detailedGrouping.teamGrouping! as any as keyof TeamGrouping;
        this.teamGroupingOptions = [
            { id: TeamGroupingValues[teamGrouping], name: this.siteCore.teamGrouping[teamGrouping] },
            { id: homeTeam.id!, name: this.getParticipantName(homeTeam) },
            { id: awayTeam.id!, name: this.getParticipantName(awayTeam) },
        ];
    }

    private getHomeAndAwayTeam(): { homeTeam: EventParticipant; awayTeam: EventParticipant } {
        const homeTeam =
            this.optionGroup.source === OfferSource.V2 && !!this.event.hybridFixtureData ? this.event.teams[3] : this.event.participants[0];
        const awayTeam =
            this.optionGroup.source === OfferSource.V2 && !!this.event.hybridFixtureData ? this.event.teams[2] : this.event.participants[1];
        return {
            homeTeam,
            awayTeam,
        };
    }

    private getParticipantName(participant: EventParticipant): string {
        const name = participant.shortName;
        if (name) {
            return this.shortTeamNamesService.clearShortName(name);
        }

        return participant.name;
    }

    private getOptionGroup(): EventOptionGroup {
        switch (this.selectedId) {
            case TeamGroupingValues.Default:
                return this.optionGroup;
            case TeamGroupingValues.AscendingOdds:
                return this.optionGroupService.getOptionGroupFilteredByOdds(this.optionGroup, OddsSorting.AscendingOrder);
            default:
                return this.optionGroupService.getOptionGroupFilteredByTeam(this.event, this.optionGroup, this.selectedId);
        }
    }
}
